/**
 * De lo que el backend sabe del gasto a un número que la agencia pueda mirar y aprobar.
 *
 * El backend manda dos cosas distintas y no las mezcla a propósito: el gasto observado del mes
 * pasado (mensual, un hecho) o el presupuesto que la cuenta tiene cargado en la plataforma (DIARIO,
 * una intención). Convertir el segundo a mensual es una decisión de la pantalla — cuántos días
 * tiene "un mes" depende de qué le vamos a mostrar al usuario, no del dato.
 */

/** Días del mes en curso. Es la ventana que el usuario tiene en la cabeza cuando ve "por mes". */
export function daysInCurrentMonth(today = new Date()) {
    return new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
}

/**
 * El presupuesto diario de la plataforma, llevado al mes.
 *
 * El resultado se redondea HACIA ARRIBA a una cifra redonda, y eso es intencional: nadie define un
 * presupuesto de 2.847.310. Un número así se lee como un cálculo nuestro y invita a corregirlo;
 * uno redondo se lee como una propuesta y se acepta.
 */
export function monthlyFromDaily(dailyAmount, today = new Date()) {
    const daily = Number(dailyAmount);
    if (!Number.isFinite(daily) || daily <= 0) return null;
    return roundToAgencyFigure(daily * daysInCurrentMonth(today));
}

/**
 * El monto redondeado a la "cifra de agencia" más cercana hacia arriba.
 *
 * La escala del redondeo acompaña al tamaño del número: mil pesos de diferencia no significan lo
 * mismo sobre 3.000 que sobre 30.000.000.
 */
export function roundToAgencyFigure(amount) {
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) return null;

    const step = roundingStepFor(value);
    return Math.ceil(value / step) * step;
}

function roundingStepFor(value) {
    if (value >= 10000000) return 1000000;
    if (value >= 1000000) return 100000;
    if (value >= 100000) return 10000;
    if (value >= 10000) return 1000;
    if (value >= 1000) return 100;
    return 1;
}

/**
 * Qué mostrarle al usuario de dónde salió el número, por cuenta.
 *
 * Se devuelve la CLAVE de i18n y no el texto: la evidencia es lo que sostiene la confianza en el
 * monto sugerido, y tiene que decir la verdad sobre su origen — un gasto observado y una
 * estimación desde el presupuesto diario no son lo mismo aunque se vean parecidos en pantalla.
 */
export function evidenceKindOf(suggestion) {
    if (!suggestion) return 'none';
    const source = sourceOf(suggestion);
    if (source === 'OBSERVED_SPEND') return 'observed';
    if (source === 'PLATFORM_BUDGET' && suggestion.daily_amount) return 'estimated';
    return 'none';
}

/**
 * Por qué no hay sugerencia, según lo que el backend dijo — y no según lo que suponemos.
 *
 * Cuando `source` es `NONE` viaja `reason` con uno de cuatro valores
 * (`core/services/ad_account_spend_service.py`), y no significan lo mismo: `DORMANT` y
 * `NO_BUDGET_FOUND` son hechos sobre la cuenta, `CHANNEL_HAS_NO_BUDGET` es un límite del canal, y
 * `UNKNOWN` es «el catálogo todavía no se trajo» — o sea, nosotros. La pantalla decía siempre «sin
 * gasto el último mes», que con `UNKNOWN` afirma algo que el payload desmiente, y `UNKNOWN` era el
 * ~84% de las filas.
 *
 * Devuelve la CLAVE de i18n, o `null` si el valor no se reconoce: ahí la fila cae en el texto de
 * siempre en vez de quedarse muda.
 */
const NONE_REASON_KEYS = {
    UNKNOWN: 'onboarding.budgets.evidence_none_unknown',
    DORMANT: 'onboarding.budgets.evidence_none_dormant',
    NO_BUDGET_FOUND: 'onboarding.budgets.evidence_none_no_budget_found',
    CHANNEL_HAS_NO_BUDGET: 'onboarding.budgets.evidence_none_channel_has_no_budget',
};

/**
 * `UNKNOWN` es el único `reason` que CADUCA, y por eso tiene dos textos.
 *
 * Los otros tres son hechos sobre la cuenta o sobre el canal: siguen siendo verdad para siempre.
 * `UNKNOWN` dice «todavía estamos leyendo esta cuenta», y eso deja de ser cierto en el instante en
 * que el poll se detiene — ahí ya no lo estamos leyendo ni lo vamos a hacer. Verificado en
 * producción el 02/09: el aviso de «estamos consultando» desaparecía y las filas quedaban afirmando
 * un «todavía» que ya nadie iba a actualizar.
 *
 * Y no es un borde: `refresh.pending` es un booleano de TODA la tabla, así que alcanza con una sola
 * cuenta que no pueda marcarse (medido: 1 de 129) para que el poll agote su tope siempre. El estado
 * de abajo es el que ve cualquier agencia con un padrón grande, no un caso raro.
 */
const UNKNOWN_STALLED_KEY = 'onboarding.budgets.evidence_none_unknown_stalled';

/**
 * Y `UNKNOWN` tiene un TERCER sentido donde nadie pide el catálogo: el modal «Desde tus cuentas
 * conectadas» del Panel no dispara `prepare-budgets` ni espera un poll (decisión de fede, 30/09: las
 * cuentas ya vinculadas a un cliente suelen tener catálogo, y hacer esperar el caso raro no vale la
 * pena). Ahí no es «todavía estamos leyendo» —no lo estamos haciendo— ni «no pudimos leer» —no lo
 * intentamos—: es que todavía no hay con qué sugerir, y va en tono neutro.
 */
const UNKNOWN_NOT_REQUESTED_KEY = 'onboarding.budgets.evidence_none_unknown_later';

export function noneReasonOf(suggestion) {
    return String(suggestion?.reason || '').toUpperCase();
}

export function evidenceNoneKeyOf(suggestion, stillPreparing = true, catalogRequested = true) {
    const reason = noneReasonOf(suggestion);
    if (reason === 'UNKNOWN' && !catalogRequested) return UNKNOWN_NOT_REQUESTED_KEY;
    if (reason === 'UNKNOWN' && !stillPreparing) return UNKNOWN_STALLED_KEY;
    return NONE_REASON_KEYS[reason] || null;
}

/**
 * ¿La ausencia de número dice algo de la cuenta, o sólo que todavía no la miramos?
 *
 * Gobierna el tono: el ámbar de advertencia y el triángulo son para lo primero. Ponerlos sobre «el
 * catálogo todavía no se trajo» le pide al usuario que se preocupe por algo nuestro.
 *
 * Con el poll ya detenido, `UNKNOWN` cambia de bando: mientras se estaba trayendo el catálogo era
 * un estado nuestro y transitorio, pero una vez que dejamos de intentarlo es un hecho que el usuario
 * necesita ver — esa fila no va a recibir monto y le toca a él decidirlo. Sin esto, la única fila
 * que exige acción sería la única sin marca.
 *
 * Donde el catálogo no se pidió (`catalogRequested = false`), `UNKNOWN` sigue siendo nuestro: no
 * hubo un intento fallido que el usuario tenga que atender.
 */
export function evidenceNoneIsAboutTheAccount(suggestion, stillPreparing = true, catalogRequested = true) {
    if (noneReasonOf(suggestion) !== 'UNKNOWN') return true;
    return catalogRequested && !stillPreparing;
}

/**
 * El origen del monto, en una sola forma.
 *
 * El backend pasó de minúsculas a MAYÚSCULAS y los dos repositorios no se despliegan a la vez, así
 * que durante esa ventana conviven las dos. Comparar contra una sola hacía que la evidencia
 * desapareciera —el número se seguía viendo, pero sin decir de dónde salía— y eso es justamente lo
 * que un presupuesto sugerido no puede permitirse: un número que no se puede discutir no se aprueba,
 * se ignora.
 */
export function sourceOf(suggestion) {
    return String(suggestion?.source || '').toUpperCase();
}

/**
 * El monto mensual que el túnel propone para una cuenta, venga de donde venga.
 *
 * Devuelve `null` cuando no hay nada en qué basarse: ahí la línea nace sin presupuesto, que es un
 * final válido del túnel y no un error — la cuenta que no gastó el mes pasado igual merece su
 * línea, y el monto se completa después.
 */
export function suggestedMonthlyFor(suggestion, today = new Date()) {
    if (!suggestion) return null;
    if (suggestion.amount !== null && suggestion.amount !== undefined) {
        return roundToAgencyFigure(suggestion.amount);
    }
    if (suggestion.daily_amount) return monthlyFromDaily(suggestion.daily_amount, today);
    return null;
}

/**
 * ¿Ya sabemos qué decir de esta cuenta?
 *
 * Es la MISMA pregunta que decide el texto de la evidencia, vista desde el paso: una cuenta está
 * resuelta cuando tiene monto sugerido o cuando el backend explicó por qué no lo tiene. El único
 * estado que no cuenta es `UNKNOWN`, que no habla de la cuenta sino de nosotros — el catálogo
 * todavía no llegó. Sin esta distinción, un contador de progreso diría "129 de 129 listas" con el
 * 84% de las filas esperando el catálogo.
 */
export function isBudgetResolved(suggestion) {
    if (!suggestion) return false;
    if (evidenceKindOf(suggestion) !== 'none') return true;
    return noneReasonOf(suggestion) !== 'UNKNOWN';
}

/**
 * Cuántas de estas cuentas ya están resueltas. Es el numerador del progreso del paso 4.
 *
 * Se cuenta sobre las cuentas que la pantalla va a mostrar y no sobre las claves que devolvió el
 * backend: una cuenta que todavía no vino en ninguna tanda no está resuelta, y contar el tamaño de
 * la respuesta la haría desaparecer del denominador en vez de aparecer como pendiente.
 */
export function resolvedBudgetsCountOf(pks, budgets) {
    if (!pks || !pks.length) return 0;
    return pks.filter(pk => isBudgetResolved(budgets?.[String(pk)])).length;
}
